import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { useAsync } from '../lib/useAsync';
import { Badge, Button, Card, EmptyState, ErrorAlert, Field, Modal, PageHeader, Pagination, Spinner } from '../components/ui';
import { LANGUAGES, PLATFORMS } from '@kontentowo/config';
import type { Brand } from '@kontentowo/shared';

const EMPTY_FORM = {
  name: '',
  website: '',
  language: 'pl',
  additionalLanguages: [] as string[],
  toneOfVoice: '',
  targetAudience: '',
  defaultCTA: '',
  description: '',
  defaultPlatforms: [] as string[],
  weeklyGoal: 5,
};

export function BrandsPage() {
  const { data, error, loading, reload } = useAsync(() => api.brands.list(), []);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<unknown>(null);

  const [editing, setEditing] = useState<Brand | null>(null);
  const [editForm, setEditForm] = useState({ name: '', description: '', website: '', language: 'pl', additionalLanguages: [] as string[], defaultPlatforms: [] as string[], weeklyGoal: 0, requireApproval: true, defaultCTA: '', toneOfVoice: '', targetAudience: '', brandPrompt: '', visualPrompt: '', colors: '', autopilotLevel: 'OFF' as string, ctaEn: '' });
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState<unknown>(null);
  const [pinsProjects, setPinsProjects] = useState<{ id: string; domain: string; brandId: string | null }[]>([]);
  const [page, setPage] = useState(1);

  const openEdit = async (brand: Brand) => {
    setEditing(brand);
    try { setPinsProjects(await api.pinsPanel.projects()); } catch { /* noop */ }
    setEditForm({
      name: brand.name,
      description: brand.description ?? '',
      website: brand.website ?? '',
      language: brand.language,
      additionalLanguages: brand.additionalLanguages ?? [],
      defaultPlatforms: brand.publishConfig.defaultPlatforms ?? [],
      weeklyGoal: brand.publishConfig.weeklyGoal ?? 0,
      requireApproval: brand.publishConfig.requireApproval,
      defaultCTA: brand.defaultCTA ?? '',
      toneOfVoice: brand.toneOfVoice ?? '',
      targetAudience: brand.targetAudience ?? '',
      brandPrompt: brand.brandPrompt ?? '',
      visualPrompt: brand.visualPrompt ?? '',
      colors: brand.colors?.join(', ') ?? '',
      autopilotLevel: brand.autopilotLevel,
      ctaEn: brand.publishConfig.ctaTranslations?.en ?? '',
    });
    setEditError(null);
  };

  const toggleEditPlatform = (platform: string) => {
    setEditForm((f) => ({
      ...f,
      defaultPlatforms: f.defaultPlatforms.includes(platform)
        ? f.defaultPlatforms.filter((p) => p !== platform)
        : [...f.defaultPlatforms, platform],
    }));
  };

  const saveEdit = async () => {
    if (!editing) return;
    setEditSaving(true);
    setEditError(null);
    try {
      const checked = new Set((window as unknown as { __pinsSel?: string[] }).__pinsSel ?? pinsProjects.filter((p) => p.brandId === editing.id).map((p) => p.id));
      for (const p of pinsProjects) {
        const on = checked.has(p.id);
        if (on && p.brandId !== editing.id) await api.pinsPanel.update(p.id, { brandId: editing.id });
        if (!on && p.brandId === editing.id) await api.pinsPanel.update(p.id, { brandId: '' });
      }
      await api.brands.update(editing.id, {
        name: editForm.name.trim() || editing.name,
        description: editForm.description.trim() || null,
        website: editForm.website.trim() || null,
        language: editForm.language as Brand['language'],
        additionalLanguages: editForm.additionalLanguages.filter((language) => language !== editForm.language) as Brand['additionalLanguages'],
        defaultCTA: editForm.defaultCTA || null,
        toneOfVoice: editForm.toneOfVoice || null,
        targetAudience: editForm.targetAudience || null,
        brandPrompt: editForm.brandPrompt || null,
        visualPrompt: editForm.visualPrompt || null,
        colors: editForm.colors ? editForm.colors.split(',').map((c) => c.trim()).filter(Boolean) : null,
        autopilotLevel: editForm.autopilotLevel as Brand['autopilotLevel'],
        publishConfig: {
          ...editing.publishConfig,
          defaultPlatforms: editForm.defaultPlatforms as Brand['publishConfig']['defaultPlatforms'],
          weeklyGoal: Number(editForm.weeklyGoal) || 0,
          requireApproval: editForm.requireApproval,
          ctaTranslations: { ...(editing.publishConfig.ctaTranslations ?? {}), en: editForm.ctaEn },
        },
      } as Partial<Brand>);
      setEditing(null);
      reload();
    } catch (err) {
      setEditError(err);
    } finally {
      setEditSaving(false);
    }
  };

  const togglePlatform = (platform: string) => {
    setForm((f) => ({
      ...f,
      defaultPlatforms: f.defaultPlatforms.includes(platform)
        ? f.defaultPlatforms.filter((p) => p !== platform)
        : [...f.defaultPlatforms, platform],
    }));
  };

  const submit = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      await api.brands.create({
        name: form.name,
        website: form.website || null,
        language: form.language as Brand['language'],
        additionalLanguages: form.additionalLanguages.filter((language) => language !== form.language) as Brand['additionalLanguages'],
        toneOfVoice: form.toneOfVoice || null,
        targetAudience: form.targetAudience || null,
        defaultCTA: form.defaultCTA || null,
        description: form.description || null,
        publishConfig: {
          weeklyGoal: Number(form.weeklyGoal) || 0,
          defaultPlatforms: form.defaultPlatforms as Brand['publishConfig']['defaultPlatforms'],
          defaultHashtags: [],
          requireApproval: true,
          similarityThreshold: 0.86,
          defaultPublisherType: 'SOCIAMONIALS',
          sociamonialsWorkspaceId: null,
          telegramChannelId: null,
          ctaTranslations: {},
        },
      } as Partial<Brand>);
      setOpen(false);
      setForm(EMPTY_FORM);
      reload();
    } catch (err) {
      setSaveError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <PageHeader
        title="Brands"
        subtitle="Marka jest podstawową jednostką Kontentowo — posiada strony, profile social, styl i konfigurację publikacji."
        actions={<Button variant="primary" onClick={() => setOpen(true)}>+ Nowa marka</Button>}
      />

      {loading && <Spinner />}
      {error && <ErrorAlert error={error} />}
      {data && data.length === 0 && <EmptyState title="Brak marek" hint="Dodaj pierwszą markę." />}

      {data && data.length > 0 && (
        <div className="grid grid--3">
          {data.slice((page - 1) * 10, page * 10).map((brand) => (
            <Card key={brand.id}>
              <div className="inline" style={{ justifyContent: 'space-between' }}>
                <h3 style={{ margin: 0 }}>
                  <Link to={`/content-studio?brand=${brand.id}`}>{brand.name}</Link>
                </h3>
                <Badge tone={brand.enabled ? 'success' : 'neutral'}>{brand.enabled ? 'active' : 'off'}</Badge>
              </div>
              <p style={{ marginTop: 8 }}>{brand.description ?? 'Brak opisu.'}</p>
              <div className="pill-list" style={{ marginTop: 10 }}>
                <Badge tone="info">{brand.language}</Badge>
                <Badge tone="neutral">autopilot: {brand.autopilotLevel}</Badge>
                {(brand.publishConfig.defaultPlatforms ?? []).map((p) => (
                  <Badge key={p} tone="neutral">
                    {p}
                  </Badge>
                ))}
              </div>
              {brand.website && (
                <div style={{ marginTop: 12 }}>
                  <a className="muted" href={brand.website} target="_blank" rel="noreferrer">
                    {brand.website}
                  </a>
                </div>
              )}
              <div className="btn-row" style={{ marginTop: 14 }}>
                <Link className="btn btn--sm" to={`/content-studio?brand=${brand.id}`}>
                  Content Studio
                </Link>
                <Link className="btn btn--sm" to={`/calendar`}>
                  Calendar
                </Link>
                <Button size="sm" variant="ghost" onClick={() => openEdit(brand)}>
                  Edytuj
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}
      {data && <Pagination page={page} pageSize={10} total={data.length} onChange={setPage} />}

      <Modal
        title="Nowa marka"
        open={open}
        onClose={() => setOpen(false)}
        footer={
          <>
            <Button onClick={() => setOpen(false)}>Anuluj</Button>
            <Button variant="primary" onClick={submit} disabled={saving || !form.name}>
              {saving ? 'Zapisywanie…' : 'Utwórz markę'}
            </Button>
          </>
        }
      >
        <ErrorAlert error={saveError} />
        <Field label="Nazwa">
          <input className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="np. RiftCells" />
        </Field>
        <div className="form-row">
          <Field label="Strona WWW">
            <input className="input" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} placeholder="https://…" />
          </Field>
          <Field label="Język">
            <select className="select" value={form.language} onChange={(e) => setForm({ ...form, language: e.target.value })}>
              {LANGUAGES.map((l) => (
                <option key={l} value={l}>
                  {l}
                </option>
              ))}
            </select>
          </Field>
        </div>
        <Field label="Dodatkowe języki treści" hint="Tworzą osobne drafty dla BloodCrimes World Events. Język główny jest pomijany.">
          <div className="form-row">{LANGUAGES.filter((language) => language !== form.language).map((language) => <label className="inline" key={language}><input type="checkbox" checked={form.additionalLanguages.includes(language)} onChange={(e) => setForm({ ...form, additionalLanguages: e.target.checked ? [...form.additionalLanguages, language] : form.additionalLanguages.filter((item) => item !== language) })} /> {language.toUpperCase()}</label>)}</div>
        </Field>
        <Field label="Opis">
          <textarea className="textarea" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        </Field>
        <div className="form-row">
          <Field label="Tone of voice">
            <input className="input" value={form.toneOfVoice} onChange={(e) => setForm({ ...form, toneOfVoice: e.target.value })} />
          </Field>
          <Field label="Grupa docelowa">
            <input className="input" value={form.targetAudience} onChange={(e) => setForm({ ...form, targetAudience: e.target.value })} />
          </Field>
        </div>
        <div className="form-row">
          <Field label="Domyślne CTA">
            <input className="input" value={form.defaultCTA} onChange={(e) => setForm({ ...form, defaultCTA: e.target.value })} />
          </Field>
          <Field label="Cel tygodniowy (posty)">
            <input className="input" type="number" value={form.weeklyGoal} onChange={(e) => setForm({ ...form, weeklyGoal: Number(e.target.value) })} />
          </Field>
        </div>
        <Field label="Domyślne platformy">
          <div className="chips">
            {PLATFORMS.filter((p) => p !== 'OTHER').map((p) => (
              <span key={p} className={`chip${form.defaultPlatforms.includes(p) ? ' chip--active' : ''}`} onClick={() => togglePlatform(p)}>
                {p}
              </span>
            ))}
          </div>
        </Field>
      </Modal>

      <Modal
        title={`Edytuj markę: ${editing?.name ?? ''}`}
        open={editing !== null}
        onClose={() => setEditing(null)}
        footer={
          <>
            <Button onClick={() => setEditing(null)}>Anuluj</Button>
            <Button variant="primary" onClick={saveEdit} disabled={editSaving || !editing}>
              {editSaving ? 'Zapisywanie…' : 'Zapisz'}
            </Button>
          </>
        }
      >
        <ErrorAlert error={editError} />
        <div className="form-row">
          <Field label="Nazwa brandu">
            <input className="input" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} />
          </Field>
          <Field label="Opis">
            <textarea className="textarea" rows={2} value={editForm.description} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} />
          </Field>
          <Field label="Strona WWW">
            <input className="input" value={editForm.website} onChange={(e) => setEditForm({ ...editForm, website: e.target.value })} />
          </Field>
          <Field label="Grupa docelowa">
            <input className="input" value={editForm.targetAudience} onChange={(e) => setEditForm({ ...editForm, targetAudience: e.target.value })} />
          </Field>
          <Field label="Brand prompt (Brand Brain)">
            <textarea className="textarea" rows={2} value={editForm.brandPrompt} onChange={(e) => setEditForm({ ...editForm, brandPrompt: e.target.value })} />
          </Field>
          <Field label="Visual prompt (styl grafik)">
            <textarea className="textarea" rows={2} value={editForm.visualPrompt} onChange={(e) => setEditForm({ ...editForm, visualPrompt: e.target.value })} />
          </Field>
          <Field label="Kolory (lista, przecinkami)">
            <input className="input" value={editForm.colors} onChange={(e) => setEditForm({ ...editForm, colors: e.target.value })} />
          </Field>
          <Field label="Content Autopilot">
            <select className="select" value={editForm.autopilotLevel} onChange={(e) => setEditForm({ ...editForm, autopilotLevel: e.target.value })}>
              {['OFF', 'ASSISTED', 'AUTO_GENERATE', 'AUTO_SCHEDULE', 'FULL_AUTOPILOT'].map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
          </Field>
          <Field label="Domeny PINS">
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', maxHeight: 160, overflow: 'auto' }}>
              {pinsProjects.map((p) => {
                const on = p.brandId === editing?.id;
                return (
                  <label key={p.id} style={{ display: 'inline-flex', gap: 4, alignItems: 'center', fontSize: 12.5 }}>
                    <input type="checkbox" defaultChecked={on} onChange={(e) => {
                      const s = new Set((window as unknown as { __pinsSel?: string[] }).__pinsSel ?? pinsProjects.filter((x) => x.brandId === editing?.id).map((x) => x.id));
                      if (e.target.checked) s.add(p.id); else s.delete(p.id);
                      (window as unknown as { __pinsSel?: string[] }).__pinsSel = [...s];
                    }} />
                    {p.domain}
                  </label>
                );
              })}
            </div>
          </Field>
        </div>
        <div className="form-row">
          <Field label="Język">
            <select className="select" value={editForm.language} onChange={(e) => setEditForm({ ...editForm, language: e.target.value })}>
              {LANGUAGES.map((l) => (
                <option key={l} value={l}>
                  {l}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Cel tygodniowy (posty)">
            <input className="input" type="number" value={editForm.weeklyGoal} onChange={(e) => setEditForm({ ...editForm, weeklyGoal: Number(e.target.value) })} />
          </Field>
        </div>
        <Field label="Dodatkowe języki treści" hint="BloodCrimes World Events dostaną osobny, natywnie napisany draft dla każdego zaznaczonego języka.">
          <div className="form-row">{LANGUAGES.filter((language) => language !== editForm.language).map((language) => <label className="inline" key={language}><input type="checkbox" checked={editForm.additionalLanguages.includes(language)} onChange={(e) => setEditForm({ ...editForm, additionalLanguages: e.target.checked ? [...editForm.additionalLanguages, language] : editForm.additionalLanguages.filter((item) => item !== language) })} /> {language.toUpperCase()}</label>)}</div>
        </Field>
        <div className="form-row">
          <Field label="Domyślne CTA">
            <input className="input" value={editForm.defaultCTA} onChange={(e) => setEditForm({ ...editForm, defaultCTA: e.target.value })} />
          </Field>
          <Field label="CTA (EN)" hint="Angielskie CTA dla treści po angielsku">
            <input className="input" value={editForm.ctaEn} onChange={(e) => setEditForm({ ...editForm, ctaEn: e.target.value })} />
          </Field>
        </div>
        <Field label="Tone of voice">
          <input className="input" value={editForm.toneOfVoice} onChange={(e) => setEditForm({ ...editForm, toneOfVoice: e.target.value })} />
        </Field>
        <Field label="Domyślne platformy (Content Studio)" hint="Odznacz, aby platforma nie była domyślnie zaznaczana.">
          <div className="chips">
            {PLATFORMS.filter((p) => p !== 'OTHER').map((p) => (
              <span key={p} className={`chip${editForm.defaultPlatforms.includes(p) ? ' chip--active' : ''}`} onClick={() => toggleEditPlatform(p)}>
                {p}
              </span>
            ))}
          </div>
        </Field>
        <label className="inline" style={{ gap: 8 }}>
          <input type="checkbox" checked={editForm.requireApproval} onChange={(e) => setEditForm({ ...editForm, requireApproval: e.target.checked })} />
          Wymagaj zatwierdzenia
        </label>
      </Modal>
    </>
  );
}
